// Initial DOM compile helpers for runtime source discovery.
// It ensures first-mount compile runs once unless forced.
import { rebuildLuisSources } from '../loader/process'

// Guard flag to run initial DOM compile only once unless force is requested.
const INITIAL_COMPILE_FLAG = '__lentystyle_initial_dom_compile_done__'

type LoaderOptions = {
  force?: boolean
}

// Public helper to process all current .luis sources in the document.
export async function loadLuisStylesheetLinks(options: LoaderOptions = {}): Promise<void> {
  // Exit immediately when runtime is executed outside browser context.
  if (!isBrowser()) {
    return
  } else {
    // Continue dom compile with first-mount guard.
  }

  await compileFromDom({ force: options.force })
}

// Compile currently mounted .luis sources with one-time initial guard.
export async function compileFromDom(options: { force?: boolean } = {}): Promise<void> {
  const force = Boolean(options.force)
  const win = window as Window & { [INITIAL_COMPILE_FLAG]?: boolean }
  // Skip compile when first-mount compile already happened and force is off.
  if (win[INITIAL_COMPILE_FLAG] && !force) {
    return
  } else {
    // Continue with first compile or forced compile.
  }

  win[INITIAL_COMPILE_FLAG] = true
  await rebuildLuisSources()
}

// Run initial DOM compile once and mark completion on window scope.
export function queueInitialRebuild(force: boolean, queueRebuild: () => void): void {
  const win = window as Window & { [INITIAL_COMPILE_FLAG]?: boolean }
  // Skip initial compile when runtime already compiled once and force is off.
  if (win[INITIAL_COMPILE_FLAG] && !force) {
    return
  } else {
    // Continue with initial queue execution.
  }

  win[INITIAL_COMPILE_FLAG] = true
  queueRebuild()
}

// Runtime utilities should run only in real browser contexts.
function isBrowser(): boolean {
  // Return true in browser-like runtime environments.
  return typeof window !== 'undefined' && typeof document !== 'undefined'
}
